// Common consumer email providers a sponsored newsletter might list as its
// contact — never the newsletter's own domain, so never worth trying as a
// logo source.
const GENERIC_EMAIL_DOMAINS = new Set([
    'gmail.com', 'yahoo.com', 'outlook.com', 'hotmail.com', 'icloud.com',
    'aol.com', 'proton.me', 'protonmail.com', 'live.com', 'msn.com'
]);

// A handful of high-frequency newsletters we're confident about the domain
// for. These get sponsored often enough across placements that hardcoding
// them meaningfully improves real-logo coverage — keep this list to names
// we're certain of, since a wrong guess here shows a wrong logo, which is
// worse than the newspaper-icon fallback (see NewsletterLogo).
const KNOWN_NEWSLETTER_DOMAINS: Record<string, string> = {
    'morning brew': 'morningbrew.com',
    'the hustle': 'thehustle.co',
    'tldr': 'tldr.tech',
    'tldr daily': 'tldr.tech',
    '1440': 'join1440.com',
    'the skimm': 'theskimm.com',
    'milk road': 'milkroad.com',
    "lenny's newsletter": 'lennysnewsletter.com',
    'stratechery': 'stratechery.com',
    'the neuron': 'theneurondaily.com',
    'sahil bloom': 'sahilbloom.com',
    'sahilbloom': 'sahilbloom.com',
    'mark manson': 'markmanson.net',
    'markmanson': 'markmanson.net',
    'axios': 'axios.com',
    'money stuff': 'bloomberg.com',
    'superhuman ai': 'superhuman.ai',
};

// Sending-subdomain prefixes ESPs commonly put in front of a newsletter's
// real domain (mail.example.com, news.example.com, ...) — stripped before
// using a domain for a logo lookup or deriving a readable name, since the
// bare prefix+domain has no favicon of its own.
const ESP_SUBDOMAIN_PREFIXES = new Set([
    'www', 'mail', 'email', 'e', 'em', 'mg', 'news', 'newsletter', 'go',
    'links', 'click', 'info', 'marketing', 'hello', 'daily', 'weekly',
    'updates', 'send', 'sendgrid', 'campaign',
]);

// Shared newsletter-platform domains — the "domain" of a beehiiv/Substack/etc
// sending address is the platform's own site, not the specific newsletter's
// brand. Showing beehiiv.com's favicon for someone's beehiiv-hosted
// newsletter would be actively wrong, not just unhelpful.
const SHARED_PLATFORM_DOMAINS = new Set([
    'beehiiv.com', 'substack.com', 'substackcdn.com', 'mailchimp.com',
    'constantcontact.com', 'convertkit.com', 'convertkit-mail.com',
    'campaign-archive.com', 'sendgrid.net', 'mailgun.org', 'klaviyomail.com',
    'ghost.io', 'buttondown.email',
]);

const normalizeNewsletterName = (name?: string): string => (name || '').trim().toLowerCase();

const looksLikeEmail = (value?: string): boolean => !!value && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

// mail.joinsuperhuman.ai -> joinsuperhuman.ai ; daily.therundown.ai -> therundown.ai
const stripEspSubdomains = (domain: string): string => {
    const labels = domain.split('.');
    while (labels.length > 2 && ESP_SUBDOMAIN_PREFIXES.has(labels[0])) {
        labels.shift();
    }
    return labels.join('.');
};

const titleCase = (value: string): string =>
    value
        .split(/[\s._-]+/)
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

/**
 * The scraper stores whichever newsletter sent the sponsored email as
 * `newsletterName` — normally a real display name ("Superhuman AI"), but
 * when a source email's From header had no display name at all, the raw
 * address itself ends up stored as the "name" (e.g.
 * "superhuman@mail.joinsuperhuman.ai"). That's true of some already-approved
 * records, not just a hypothetical: rendering it straight shows an email
 * address in place of a name, with no logo (the raw address's domain
 * usually isn't the newsletter's own site).
 *
 * This derives a presentable {name, domain} for any placement regardless of
 * which field ended up holding the email — prefer a real name when one
 * exists, and when the only signal is an email address, turn it into a
 * cleaned domain (for the logo) and a readable name, never displaying the
 * raw address itself.
 */
export const deriveNewsletterDisplay = (
    newsletterName?: string,
    emailAddress?: string
): { name: string; domain?: string } => {
    const cleanName = (newsletterName || '').trim();

    // Real name already present — use it, still try to find a logo domain
    // (falling back to the separate emailAddress field here, same as
    // guessNewsletterDomain always has).
    if (cleanName && !looksLikeEmail(cleanName)) {
        return { name: cleanName, domain: guessNewsletterDomain(cleanName, emailAddress) };
    }

    // cleanName itself is email-shaped — that's the actual bug being fixed
    // here, and it's provably the newsletter's own sending address (that's
    // why it ended up in the name field in the first place). Derive from
    // THAT, not the separate emailAddress field: the two aren't always the
    // same contact — e.g. one already-live record has newsletterName as the
    // newsletter's own beehiiv-hosted sending address, but emailAddress
    // populated with the *sponsor's* contact email on an unrelated domain,
    // which would otherwise get misattributed to the newsletter here.
    const emailSource = looksLikeEmail(cleanName) ? cleanName : (emailAddress && emailAddress.trim()) || '';

    // Only an email-shaped string to work with (as the name, and/or as a
    // separate contact email) — derive both name and domain from it rather
    // than ever displaying the address itself.
    if (emailSource.includes('@')) {
        const [localPart, rawDomain] = emailSource.split('@');
        const domain = rawDomain?.trim().toLowerCase();
        const cleanedDomain = domain ? stripEspSubdomains(domain) : undefined;
        const domainIsUsable = !!cleanedDomain
            && !GENERIC_EMAIL_DOMAINS.has(cleanedDomain)
            && !SHARED_PLATFORM_DOMAINS.has(cleanedDomain);

        if (domainIsUsable && cleanedDomain) {
            const known = Object.entries(KNOWN_NEWSLETTER_DOMAINS).find(([, d]) => d === cleanedDomain);
            const derivedName = known ? titleCase(known[0]) : titleCase(cleanedDomain.split('.')[0]);
            return { name: derivedName, domain: cleanedDomain };
        }

        // Domain isn't a usable brand signal (shared ESP platform, or
        // missing) — the local part is often still descriptive
        // ("alternative-investing-report@mail.beehiiv.com").
        const localName = localPart ? titleCase(localPart) : '';
        return { name: localName || 'Unnamed Newsletter', domain: undefined };
    }

    return { name: cleanName || 'Unnamed Newsletter', domain: undefined };
};

/**
 * Best-effort guess at a sponsored newsletter's own domain, so its card can
 * show a real favicon the same way CompanyLogo does for the sponsor side.
 * Sponsored-newsletter placements have no domain field in the data model —
 * this checks a small curated list of well-known newsletters first (most
 * reliable), then falls back to the placement's own contact email domain
 * (skipping generic consumer providers, which are never the newsletter's
 * own domain). Returns undefined when neither source is available or
 * trustworthy — callers should render NewsletterLogo's icon fallback then,
 * rather than guess further and risk showing the wrong logo.
 */
export const guessNewsletterDomain = (newsletterName?: string, emailAddress?: string): string | undefined => {
    const known = KNOWN_NEWSLETTER_DOMAINS[normalizeNewsletterName(newsletterName)];
    if (known) return known;

    if (emailAddress && emailAddress.includes('@')) {
        const rawDomain = emailAddress.split('@')[1]?.trim().toLowerCase();
        const domain = rawDomain ? stripEspSubdomains(rawDomain) : undefined;
        if (domain && !GENERIC_EMAIL_DOMAINS.has(domain) && !SHARED_PLATFORM_DOMAINS.has(domain)) return domain;
    }

    return undefined;
};
